import { formatMinorUnitsInr } from './billing-entry-view';
import {PortalApiError} from '@/lib/portal-api';
import { AcSelect } from '@/dashboard/ui/FormControl';
import { AcButton } from '@/dashboard/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { previewBillCollection, type BillCollectionPreview } from '@/dashboard/api';

export function useBillCollectionReview(
  clinicId: number | null,
  patientId: string | null,
  amountMinorUnits: number | null,
  formIdentity: string,
  automatic = false,
) {
  const scope = JSON.stringify([clinicId, patientId]);
  const [selected, choose] = useState({ scope, value: 'oldest' });
  const selection = selected.scope === scope ? selected.value : 'oldest';
  const setSelection = (value: string) => choose({ scope, value });
  const sequence = useRef(0);
  const intent =
    selection === 'oldest'
      ? { mode: 'oldest_unpaid' as const }
      : { mode: 'selected_bill' as const, billId: Number(selection) };
  const identity = JSON.stringify([clinicId, patientId, amountMinorUnits, selection, formIdentity]);
  const current = useRef(identity);
  current.current = identity;
  const [review, setReview] = useState<{ identity: string; value: BillCollectionPreview } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choices, setChoices] = useState<{
    scope: string;
    bills: BillCollectionPreview['eligibleBills'];
  } | null>(null);
  const value = review?.identity === identity ? review.value : null;
  const refresh = useCallback(async () => {
    if (
      clinicId === null ||
      patientId === null ||
      amountMinorUnits === null ||
      amountMinorUnits <= 0
    )
      return;
    setBusy(true);
    setError(null);
    const ticket = identity;
    const request = ++sequence.current;
    try {
      const next = await previewBillCollection({
        clinicId,
        patientId,
        amountMinorUnits,
        allocationIntent:
          selection === 'oldest'
            ? { mode: 'oldest_unpaid' }
            : { mode: 'selected_bill', billId: Number(selection) },
      });
      if (current.current !== ticket || request !== sequence.current) return;
      setChoices({ scope, bills: next.eligibleBills });
      setReview({ identity: ticket, value: next });
    } catch (e) {
      if (current.current === ticket && request === sequence.current) {
        setReview(null);
        setError(e instanceof PortalApiError&&e.status===401?'Your session has expired. Sign in again before checking bills.':e instanceof PortalApiError&&e.status===403?'You do not have permission to check these bills.':'Couldn’t check bills. Try again.');
      }
    } finally {
      if (request === sequence.current) setBusy(false);
    }
  }, [clinicId, patientId, amountMinorUnits, identity, scope, selection]);
  useEffect(() => {
    if (!automatic) return;
    const timer = setTimeout(() => {
      void refresh();
    }, 250);
    return () => clearTimeout(timer);
  }, [automatic, refresh]);
  return {
    automatic,
    selection,
    setSelection,
    value,
    busy,
    error,
    refresh,
    choices: choices?.scope === scope ? choices.bills : [],
    canReview:
      clinicId !== null && patientId !== null && amountMinorUnits !== null && amountMinorUnits > 0,
    command: value ? { allocationIntent: intent, previewDigest: value.digest } : null,
  };
}
// THE ONE money format on the collection surfaces (no stray paise on whole rupees).
const rupees = (n: number) => formatMinorUnitsInr(n);
export function BillCollectionReview({
  review,
}: {
  review: ReturnType<typeof useBillCollectionReview>;
}) {
  return (
    <section aria-label="Bill allocation" data-testid="bill-collection-review">
      {review.choices.length > 1 ? (
        <label>
          Apply payment to{' '}
          <AcSelect value={review.selection} onChange={(e) => review.setSelection(e.target.value)}>
            <option value="oldest">Oldest unpaid first</option>
            {review.choices.map((b) => (
              <option key={b.billId} value={b.billId}>
                Bill #{b.billId} · {rupees(b.dueMinorUnits)} unpaid
              </option>
            ))}
          </AcSelect>
        </label>
      ) : null}
      {!review.automatic || review.error ? (
        <AcButton
          type="button"
          disabled={!review.canReview || review.busy}
          onClick={() => void review.refresh()}
        >
          {review.busy
            ? 'Checking bills…'
            : review.automatic
              ? 'Retry'
              : 'Review payment'}
        </AcButton>
      ) : review.busy ? (
        <p role="status">Checking bills…</p>
      ) : null}
      {review.error ? <p role="alert">{review.error}</p> : null}
      {review.value ? (
        <div role="status">
          {review.value.eligibleBills.length > 1 ? (
            <>
              <p>
                Unpaid finalized bills:{' '}
                <strong>{rupees(review.value.unpaidBeforeMinorUnits)}</strong>
              </p>
              <table>
                <caption>Proposed bill allocation</caption>
                <thead>
                  <tr>
                    <th>Bill</th>
                    <th>To apply</th>
                    <th>Balance after</th>
                  </tr>
                </thead>
                <tbody>
                  {review.value.allocations.map((a) => (
                    <tr key={a.billId}>
                      <td>#{a.billId}</td>
                      <td>{rupees(a.amountMinorUnits)}</td>
                      <td>{rupees(a.dueAfterMinorUnits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : review.value.allocations.length === 1 ? (
            <p className="bill-allocation-summary">
              <span>
                Bill <strong>#{review.value.allocations[0].billId}</strong>
              </span>
              <span>
                Balance after{' '}
                <strong>{rupees(review.value.allocations[0].dueAfterMinorUnits)}</strong>
              </span>
            </p>
          ) : null}
          {review.value.unappliedCreditMinorUnits > 0 ? <p>This payment: <strong>{rupees(review.value.unappliedCreditMinorUnits)}</strong> not applied to a bill</p> : null}

        </div>
      ) : null}
    </section>
  );
}
